import { motion } from 'framer-motion';
import { companyInfo, coreValues } from '../data/companyData';
import Footer from '../components/Footer';

const AboutPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  const getIcon = (iconName) => {
    const iconMap = {
      'Users': 'fas fa-users',
      'Award': 'fas fa-award',
      'Lightbulb': 'fas fa-lightbulb',
      'Heart': 'fas fa-heart',
      'TrendingUp': 'fas fa-chart-line',
      'Shield': 'fas fa-shield-alt'
    };
    return iconMap[iconName] || 'fas fa-star';
  };

  return (
    <div>
      <section className="pt-32 pb-20 bg-gradient-to-br from-tev-green to-tev-green-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-bold mb-6">About {companyInfo.name}</h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Our commitment to food security and sustainable agriculture
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center mb-16">
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
            >
              <h2 className="text-3xl font-semibold text-gray-900 mb-6">Who We Are</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our focus is on food value chains. Through this, our business exists to solve one of the most consequential of human needs. 
                We are therefore committed to contributing to Africa's food security.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                From seed propagation, irrigated agriculture, agro-processing and value addition, we intend to be a dependable partner in the 
                production of high quality, safe and nutritious food products.
              </p>
              
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Core Activities</h3>
                <div className="grid grid-cols-2 gap-4">
                  {['Seed Propagation', 'Irrigated Agriculture', 'Agro-Processing', 'Value Addition'].map((item, index) => (
                    <div key={index} className="flex items-center">
                      <i className="fas fa-check-circle text-tev-green mr-3"></i>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              className="space-y-6"
            >
              <div className="relative">
                <img 
                  src="/images/nice.jpg" 
                  alt="Who We Are" 
                  className="rounded-lg shadow-lg w-full object-cover h-64"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
              </div>
              <div className="relative">
                <img 
                  src="/images/who we are.jpg" 
                  alt="Our Operations" 
                  className="rounded-lg shadow-lg w-full object-cover h-64"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
              </div>
            </motion.div>
          </div>

          {/* Core Values Section */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mt-16"
          >
            <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">Our Core Values</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {coreValues.map((value, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-50 p-6 rounded-lg text-center card-hover"
                  variants={itemVariants}
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${getIcon(value.icon)} text-tev-green text-2xl`}></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Mission, Vision & Values Section */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mt-20"
          >
            <h2 className="text-3xl font-semibold text-gray-900 mb-12 text-center">Our Mission, Vision & Values</h2>
            
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <motion.div
                variants={itemVariants}
                className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-tev-green text-white p-3 rounded-full mr-4">
                    <i className="fas fa-bullseye text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900">Our Mission</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Our mission is to leverage our resources and capabilities to contribute towards food security at local, national and regional 
                  level, in a manner that is sustainable.
                </p>
              </motion.div>
              
              <motion.div
                variants={itemVariants}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-blue-600 text-white p-3 rounded-full mr-4">
                    <i className="fas fa-eye text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900">Our Vision</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Our vision is to attain scale and market leadership in our value chains, which would allow us to contribute effectively and 
                  meaningfully to realization of a better world for our communities.
                </p>
              </motion.div>
            </div>
            
            {/* Core Values */}
            <motion.div
              variants={itemVariants}
              className="bg-gray-50 p-8 rounded-xl"
            >
              <div className="flex items-center mb-6">
                <div className="bg-amber-600 text-white p-3 rounded-full mr-4">
                  <i className="fas fa-heart text-2xl"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Our Values</h3>
              </div>
              <p className="text-gray-700 mb-6">Our core values drive everything we do. They are:</p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Respect, support and value for our people',
                  'Going extra mile for our customers and partners',
                  'Thinking outside the box',
                  'Caring for our communities and natural environment',
                  'Delivering long-term value',
                  'Demonstrating leadership and integrity in all our dealings'
                ].map((value, index) => (
                  <div key={index} className="flex items-start">
                    <i className="fas fa-check-circle text-tev-green mr-3 mt-1"></i>
                    <span className="text-gray-700">{value}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Partnerships Section */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mt-20"
          >
            <h2 className="text-3xl font-semibold text-gray-900 mb-12 text-center">Our Partnerships</h2>
            
            {/* Outgrowers Section */}
            <motion.div
              variants={itemVariants}
              className="mb-16"
            >
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="flex items-center mb-6">
                    <div className="bg-green-600 text-white p-3 rounded-full mr-4">
                      <i className="fas fa-users text-2xl"></i>
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900">Our Outgrowers</h3>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Among our core values is caring for our communities and collaborating with them as partners for growth. As they succeed, so do we. To that end, we are making significant investments in capacitating producers in several districts in Malawi, to be our outgrowers.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Through this partnership, we support them with inputs, extension support and climate resilient solutions such as irrigation services, to maximize their potential for success.
                  </p>
                </div>
                <motion.div
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src="/images/Partner.jpg" 
                    alt="Outgrowers Partnership" 
                    className="rounded-lg shadow-lg w-full h-64 object-cover"
                  />
                </motion.div>
              </div>
            </motion.div>
            
            {/* Research & Development Section */}
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl"
            >
              <div className="flex items-center mb-6">
                <div className="bg-blue-600 text-white p-3 rounded-full mr-4">
                  <i className="fas fa-microscope text-2xl"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Research & Development Backstopping</h3>
              </div>
              <p className="text-gray-700 mb-6">
                Our association with following global research institutions has proved to be essential for our agronomy activities:
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center mb-4">
                    <i className="fas fa-seedling text-green-600 text-xl mr-3"></i>
                    <h4 className="font-semibold text-gray-900">Common Beans</h4>
                  </div>
                  <p className="text-gray-600">The Alliance of Bioversity International and CIAT</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center mb-4">
                    <i className="fas fa-leaf text-green-600 text-xl mr-3"></i>
                    <h4 className="font-semibold text-gray-900">Cassava</h4>
                  </div>
                  <p className="text-gray-600">The International Institute of Tropical Agriculture (IITA)</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center mb-4">
                    <i className="fas fa-circle text-amber-600 text-xl mr-3"></i>
                    <h4 className="font-semibold text-gray-900">Peanuts</h4>
                  </div>
                  <p className="text-gray-600">The International Crops Research Institute for Semi-Arid Tropics (ICRISAT)</p>
                </div>
              </div>
              <p className="text-gray-700 mt-6 italic">
                Through these partnerships, we have access to latest research, technologies and best practices in seed multiplication, as well as agronomy generally.
              </p>
            </motion.div>
          </motion.div>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mt-20"
          >
            <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">Our Operations</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                variants={itemVariants}
                className="text-center"
              >
                <img 
                  src="/images/seeds.jpg" 
                  alt="Seed Propagation" 
                  className="rounded-lg shadow-md mb-4 w-full h-48 object-cover"
                />
                <h3 className="font-semibold text-gray-900 mb-2">Seed Propagation</h3>
                <p className="text-gray-600">Quality seed development and propagation for sustainable agriculture</p>
              </motion.div>
              
              <motion.div
                variants={itemVariants}
                className="text-center"
              >
                <img 
                  src="/images/irrigation.jpg" 
                  alt="Irrigated Agriculture" 
                  className="rounded-lg shadow-md mb-4 w-full h-48 object-cover"
                />
                <h3 className="font-semibold text-gray-900 mb-2">Irrigated Agriculture</h3>
                <p className="text-gray-600">Advanced irrigation systems for optimal crop growth</p>
              </motion.div>
              
              <motion.div
                variants={itemVariants}
                className="text-center"
              >
                <img 
                  src="/images/process.jpg" 
                  alt="Agro-Processing" 
                  className="rounded-lg shadow-md mb-4 w-full h-48 object-cover"
                />
                <h3 className="font-semibold text-gray-900 mb-2">Agro-Processing</h3>
                <p className="text-gray-600">Modern facilities for value addition and quality assurance</p>
              </motion.div>
            </div>
          </motion.div>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mt-20"
          >
            <h2 className="text-3xl font-semibold text-gray-900 mb-12 text-center">Our Leaders and Team</h2>
            
            {/* Board of Directors */}
            <motion.div
              variants={itemVariants}
              className="mb-16"
            >
              <div className="flex items-center mb-8">
                <div className="bg-purple-600 text-white p-3 rounded-full mr-4">
                  <i className="fas fa-user-tie text-2xl"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Our Board of Directors</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { name: 'Mr Stan Chikakuda', role: 'Executive Chair' },
                  { name: 'Mr Robin Kilner', role: 'Independent non-executive' },
                  { name: 'Mr Patrick Staniland', role: 'Independent non-executive' }
                ].map((director, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white p-6 rounded-lg shadow-md text-center"
                  >
                    <div className="bg-gray-200 w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <i className="fas fa-user text-gray-500 text-3xl"></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">{director.name}</h4>
                    <p className="text-gray-600 text-sm">{director.role}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Executive Committee */}
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-xl"
            >
              <div className="flex items-center mb-8">
                <div className="bg-purple-600 text-white p-3 rounded-full mr-4">
                  <i className="fas fa-users-cog text-2xl"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Executive Committee (ExCo)</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  'Andrew Chinguwo',
                  'Veronica Kagonegone',
                  'Lennick Makause'
                ].map((member, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white p-6 rounded-lg shadow-md text-center"
                  >
                    <div className="bg-purple-100 w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <i className="fas fa-user text-purple-600 text-2xl"></i>
                    </div>
                    <h4 className="font-semibold text-gray-900">{member}</h4>
                    <p className="text-purple-600 text-sm font-medium">Executive Committee Member</p>
                  </motion.div>
                ))}
              </div>
              <p className="text-gray-700 mt-6 text-center">
                Our team is organized by departments with Heads of Department leading their respective areas, working collaboratively to achieve our mission and vision.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
